import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../components/Sidebar.jsx";
import { t } from "../i18n";
import { fetchBillingStatus, startPortal } from "../services/billing.js";
import { track } from "../services/analytics.js";
import "./DecorEditor.css";

import { buildDownloadName, downloadFile } from "../utils/download";

function toProxyUrl(url) {
  if (!url) return url;
  // Route cross-origin URLs (e.g. GCS) through backend proxy to satisfy canvas CORS requirements
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return `/api/fast/media/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

function buildPickerUrl(target, returnPath, multi, type = "image") {
  const params = new URLSearchParams({
    picker: "1",
    target,
    type,
    multi: multi ? "1" : "0",
    return: returnPath
  });
  return `/media?${params.toString()}`;
}
// References are now loaded dynamically from the API

export default function DecorEditor() {
  const pickerTarget = "decor_base";
  const pickerUrl = buildPickerUrl(pickerTarget, "/decor", false, "image");
  const [activeProjectId, setActiveProjectId] = useState("");
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [billingError, setBillingError] = useState("");
  const [mediaItems, setMediaItems] = useState([]);
  const [baseMedia, setBaseMedia] = useState(null);
  const [versionItems, setVersionItems] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [results, setResults] = useState([]);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareValue, setCompareValue] = useState(50);
  const [compareImageUrl, setCompareImageUrl] = useState("");
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [drawColor, setDrawColor] = useState("#FF0000");
  const [drawBrushSize, setDrawBrushSize] = useState(4);
  const [drawVisible, setDrawVisible] = useState(true);
  const drawBufferRef = useRef(null);
  const [refTab, setRefTab] = useState("palettes");
  const [selectedRefs, setSelectedRefs] = useState([]);
  const [notes, setNotes] = useState("");
  const [variantCount, setVariantCount] = useState(1);
  const [zoomItem, setZoomItem] = useState(null);
  const [intensity, setIntensity] = useState(60);
  const [refWarning, setRefWarning] = useState("");
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0, visible: false });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [cropMode, setCropMode] = useState(false);
  const [cropApplied, setCropApplied] = useState(false);
  const [cropRect, setCropRect] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [contrastValue, setContrastValue] = useState(100);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [allReferences, setAllReferences] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [showRefModal, setShowRefModal] = useState(false);
  const [refModalMode, setRefModalMode] = useState("create"); // "create" or "edit"
  const [editingRef, setEditingRef] = useState(null);
  const [refFormData, setRefFormData] = useState({
    name: "",
    type: "palette",
    colors: ["#FFFFFF", "#000000"],
    tone: [],
    tag: "",
    image_url: "",
    description: ""
  });
  const [refImageUploading, setRefImageUploading] = useState(false);
  const [refImageError, setRefImageError] = useState("");
  const [refDropUploading, setRefDropUploading] = useState(false);
  const [isRefDragging, setIsRefDragging] = useState(false);

  const canvasWrapRef = useRef(null);
  const baseCanvasRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const compareCanvasRef = useRef(null);
  const imageRef = useRef(null);
  const compareImageRef = useRef(null);
  const imageRectRef = useRef(null);
  const cropDragRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);

  const maxRefs = 5;
  const baseImageUrl = baseMedia?.url || "";
  const canGenerate = Boolean(baseImageUrl) && !generating;
  const estimatedCost = useMemo(() => Math.max(1, Math.round(variantCount * 1.2)), [variantCount]);

  const paletteList = useMemo(
    () => allReferences.filter((ref) => ref.type === "palette"),
    [allReferences]
  );
  const textureList = useMemo(
    () => allReferences.filter((ref) => ref.type === "texture"),
    [allReferences]
  );
  const furnitureList = useMemo(
    () => allReferences.filter((ref) => ref.type === "furniture"),
    [allReferences]
  );

  const currentRefs = refTab === "palettes" ? paletteList : refTab === "textures" ? textureList : furnitureList;
  const displayedResults = useMemo(() => mediaItems.slice(0, 6), [mediaItems]);
  const creditsLabel =
    typeof creditsRemaining === "number" ? `${t("decor.editor.credits")}: ${creditsRemaining}` : t("decor.editor.credits");
  const hasEditChanges =
    rotation % 360 !== 0 ||
    contrastValue !== 100 ||
    Boolean(cropRect.width && cropRect.height);

  async function ensureImageLoaded() {
    if (imageRef.current?.width && imageRef.current?.height) return imageRef.current;
    if (!baseImageUrl) return null;
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = baseImageUrl;
    });
    imageRef.current = img;
    renderBase();
    ensureDrawBuffer();
    renderDrawLayer();
    return img;
  }

  function resizeCanvases() {
    const wrapper = canvasWrapRef.current;
    if (!wrapper) return;
    const cw = wrapper.clientWidth || 0;
    const ch = wrapper.clientHeight || 0;
    const dpr = window.devicePixelRatio || 1;
    [baseCanvasRef.current, compareCanvasRef.current, drawCanvasRef.current].forEach((canvas) => {
      if (!canvas) return;
      canvas.width = Math.max(1, Math.floor(cw * dpr));
      canvas.height = Math.max(1, Math.floor(ch * dpr));
      canvas.style.width = `${cw}px`;
      canvas.style.height = `${ch}px`;
    });
  }

  function renderBase() {
    const canvas = baseCanvasRef.current;
    const wrapper = canvasWrapRef.current;
    if (!canvas || !wrapper) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const cw = wrapper.clientWidth || 0;
    const ch = wrapper.clientHeight || 0;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    const img = imageRef.current;
    if (!img) return;

    // Apply zoom
    const scale = Math.min(cw / img.width, ch / img.height) * zoom;
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const dx = (cw - drawW) / 2;
    const dy = (ch - drawH) / 2;

    // Apply rotation
    ctx.save();
    const shouldCrop = (cropMode || cropApplied) && cropRect.width && cropRect.height;
    if (shouldCrop) {
      ctx.beginPath();
      ctx.rect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);
      ctx.clip();
    }
    ctx.filter = `contrast(${contrastValue}%)`;
    ctx.translate(cw / 2, ch / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-cw / 2, -ch / 2);

    ctx.drawImage(img, dx, dy, drawW, drawH);
    ctx.filter = "none";
    ctx.restore();

    imageRectRef.current = { x: dx, y: dy, w: drawW, h: drawH };
  }

  function renderCompare() {
    const canvas = compareCanvasRef.current;
    const wrapper = canvasWrapRef.current;
    if (!canvas || !wrapper) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const cw = wrapper.clientWidth || 0;
    const ch = wrapper.clientHeight || 0;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    const img = compareImageRef.current;
    if (!img) return;

    // Apply zoom (same as renderBase)
    const scale = Math.min(cw / img.width, ch / img.height) * zoom;
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const dx = (cw - drawW) / 2;
    const dy = (ch - drawH) / 2;

    // Apply rotation (same as renderBase)
    ctx.save();
    const shouldCrop = (cropMode || cropApplied) && cropRect.width && cropRect.height;
    if (shouldCrop) {
      ctx.beginPath();
      ctx.rect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);
      ctx.clip();
    }
    ctx.filter = `contrast(${contrastValue}%)`;
    ctx.translate(cw / 2, ch / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-cw / 2, -ch / 2);

    ctx.drawImage(img, dx, dy, drawW, drawH);
    ctx.filter = "none";
    ctx.restore();
  }

  function getPointerPos(event) {
    const canvas = event.currentTarget || drawCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  function toImagePoint(point) {
    const img = imageRef.current;
    const rect = imageRectRef.current;
    if (!img || !rect || !point) return null;
    if (point.x < rect.x || point.y < rect.y || point.x > rect.x + rect.w || point.y > rect.y + rect.h) {
      return null;
    }
    const scaleX = img.width / rect.w;
    const scaleY = img.height / rect.h;
    return {
      x: (point.x - rect.x) * scaleX,
      y: (point.y - rect.y) * scaleY
    };
  }

  // ── Drawing canvas helpers ──
  function ensureDrawBuffer() {
    const img = imageRef.current;
    const targetW = (img && img.naturalWidth) ? img.naturalWidth : 1024;
    const targetH = (img && img.naturalHeight) ? img.naturalHeight : 1024;
    const buf = drawBufferRef.current;
    if (buf && buf.width === targetW && buf.height === targetH) return buf;
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    if (buf) {
      const ctx = canvas.getContext("2d");
      ctx.drawImage(buf, 0, 0);
    }
    drawBufferRef.current = canvas;
    return canvas;
  }

  function renderDrawLayer() {
    const canvas = drawCanvasRef.current;
    const wrapper = canvasWrapRef.current;
    const buf = drawBufferRef.current;
    if (!canvas || !wrapper) return;
    const cw = wrapper.clientWidth || 0;
    const ch = wrapper.clientHeight || 0;
    const dpr = window.devicePixelRatio || 1;
    const pw = Math.floor(cw * dpr);
    const ph = Math.floor(ch * dpr);
    if (canvas.width !== pw || canvas.height !== ph) {
      canvas.width = pw;
      canvas.height = ph;
      canvas.style.width = `${cw}px`;
      canvas.style.height = `${ch}px`;
    }
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    if (!drawVisible || !buf) return;
    const rect = imageRectRef.current;
    if (!rect) return;
    const shouldCrop = (cropMode || cropApplied) && cropRect.width && cropRect.height;
    if (shouldCrop) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(cropRect.x, cropRect.y, cropRect.width, cropRect.height);
      ctx.clip();
    }
    ctx.drawImage(buf, rect.x, rect.y, rect.w, rect.h);
    if (shouldCrop) {
      ctx.restore();
    }
  }

  function drawAtPoint(point, isStart) {
    const buf = ensureDrawBuffer();
    if (!buf || !point) return;
    const img = imageRef.current;
    const rect = imageRectRef.current;
    if (!img || !rect) return;
    const ctx = buf.getContext("2d");
    const scaleX = buf.width / rect.w;
    const scaleY = buf.height / rect.h;
    const bx = (point.x - rect.x) * scaleX;
    const by = (point.y - rect.y) * scaleY;
    const size = Math.max(1, drawBrushSize * scaleX);
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = size;
    ctx.strokeStyle = drawColor;
    ctx.fillStyle = drawColor;
    ctx.globalAlpha = 1;
    if (isStart || !lastPointRef.current) {
      ctx.beginPath();
      ctx.arc(bx, by, size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const lx = (lastPointRef.current.x - rect.x) * scaleX;
      const ly = (lastPointRef.current.y - rect.y) * scaleY;
      ctx.beginPath();
      ctx.moveTo(lx, ly);
      ctx.lineTo(bx, by);
      ctx.stroke();
    }
    ctx.restore();
    renderDrawLayer();
  }

  function clearDrawing() {
    const buf = drawBufferRef.current;
    if (buf) {
      const ctx = buf.getContext("2d");
      ctx.clearRect(0, 0, buf.width, buf.height);
    }
    renderDrawLayer();
  }

  function hasDrawContent() {
    const buf = drawBufferRef.current;
    if (!buf) return false;
    const ctx = buf.getContext("2d");
    const data = ctx.getImageData(0, 0, buf.width, buf.height).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) return true;
    }
    return false;
  }

  async function mergeDrawingWithBase() {
    const buf = drawBufferRef.current;
    const img = imageRef.current;
    if (!buf || !img) return { url: "", assetId: "" };
    const mergeCanvas = document.createElement("canvas");
    mergeCanvas.width = img.naturalWidth || img.width;
    mergeCanvas.height = img.naturalHeight || img.height;
    const ctx = mergeCanvas.getContext("2d");
    ctx.drawImage(img, 0, 0, mergeCanvas.width, mergeCanvas.height);
    ctx.drawImage(buf, 0, 0, mergeCanvas.width, mergeCanvas.height);
    const blob = await new Promise((resolve) => mergeCanvas.toBlob(resolve, "image/png"));
    if (!blob) return { url: "", assetId: "" };
    const formData = new FormData();
    formData.append("files", blob, `sketch_merge_${Date.now()}.png`);
    const res = await fetch("/api/fast/media/uploads", { method: "POST", body: formData });
    if (!res.ok) return { url: "", assetId: "" };
    const data = await res.json().catch(() => ({}));
    const items = Array.isArray(data.items) ? data.items : [];
    return { url: items[0]?.url || "", assetId: items[0]?.id || items[0]?.asset_id || "" };
  }

  function handleZoomIn() {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  }

  function handleZoomOut() {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  }

  function handleFit() {
    setZoom(1);
  }

  function handleRotateClockwise() {
    setRotation((prev) => (prev + 90) % 360);
  }

  function handleRotateCounterClockwise() {
    setRotation((prev) => (prev - 90 + 360) % 360);
  }

  function clampCropRect(nextRect) {
    const bounds = imageRectRef.current;
    if (!bounds) return nextRect;
    const minSize = 40;
    let { x, y, width, height } = nextRect;
    width = Math.max(minSize, width);
    height = Math.max(minSize, height);
    const maxX = bounds.x + bounds.w - width;
    const maxY = bounds.y + bounds.h - height;
    x = Math.max(bounds.x, Math.min(x, maxX));
    y = Math.max(bounds.y, Math.min(y, maxY));
    width = Math.min(width, bounds.w);
    height = Math.min(height, bounds.h);
    return { x, y, width, height };
  }

  async function handleSaveEdits() {
    if (!baseMedia) {
      setEditError(t("decor.editor.err.noMediaSelected"));
      return;
    }
    const img = imageRef.current;
    const rect = imageRectRef.current;
    if (!img || !rect) {
      setEditError(t("decor.editor.err.imageUnavailable"));
      return;
    }
    const hasCrop = cropRect.width && cropRect.height;
    const hasRotate = rotation % 360 !== 0;
    const hasContrast = contrastValue !== 100;
    if (!hasCrop && !hasRotate && !hasContrast) {
      setEditError(t("decor.editor.err.noEditToApply"));
      return;
    }
    setEditSaving(true);
    setEditError("");
    // Ensure we have a media item (creates one from orphan asset if needed)
    const ensured = await ensureBaseMediaItem();
    const mediaItemId = ensured?.mediaItemId || baseMedia?.mediaItemId || "";
    const mediaVersionId = ensured?.mediaVersionId || baseMedia?.mediaVersionId || "";
    if (!mediaItemId || !mediaVersionId) {
      setEditError(t("decor.editor.err.noMediaSelected"));
      setEditSaving(false);
      return;
    }
    const scaleX = rect.w ? img.width / rect.w : 1;
    const scaleY = rect.h ? img.height / rect.h : 1;
    const payload = {
      media_item_id: mediaItemId,
      media_version_id: mediaVersionId,
      rotate_deg: rotation,
      contrast: contrastValue
    };
    if (hasCrop) {
      payload.crop_x = Math.round((cropRect.x - rect.x) * scaleX);
      payload.crop_y = Math.round((cropRect.y - rect.y) * scaleY);
      payload.crop_width = Math.round(cropRect.width * scaleX);
      payload.crop_height = Math.round(cropRect.height * scaleY);
    }
    try {
      const res = await fetch("/api/fast/media/transform", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || t("decor.editor.err.mediaTransform"));
      }
      setBaseMedia((prev) => (prev ? {
        ...prev,
        url: data.url || prev.url,
        thumbnailUrl: prev.thumbnailUrl,
        mediaVersionId: data.media_version_id || prev.mediaVersionId
      } : prev));
      setRotation(0);
      setContrastValue(100);
      setCropMode(false);
      setCropApplied(false);
      setCropRect({ x: 0, y: 0, width: 0, height: 0 });
    } catch (err) {
      setEditError(err.message || t("decor.editor.err.mediaTransform"));
    } finally {
      setEditSaving(false);
    }
  }

  function handleCropApply() {
    if (!baseImageUrl || !cropRect.width || !cropRect.height) return;
    // TODO: Apply crop to image
    setCropMode(false);
  }

  function handleCropCancel() {
    setCropMode(false);
    setCropRect({ x: 0, y: 0, width: 0, height: 0 });
  }

  function buildPrompt() {
    const intensityText = intensity >= 80 ? "radical" : intensity >= 50 ? "modere" : "subtil";
    const refNames = selectedRefs.map((ref) => ref.name).filter(Boolean);
    const paletteInfo = selectedRefs
      .filter((ref) => ref.type === "palette")
      .map((ref) => (Array.isArray(ref.colors) ? ref.colors.join(", ") : ""))
      .filter(Boolean);
    const styleHints = [];
    if (refNames.length) styleHints.push(`references: ${refNames.join(", ")}`);
    if (paletteInfo.length) styleHints.push(`palette: ${paletteInfo.join(" / ")}`);
    const extra = notes.trim();
    return [
      extra ? `Notes: ${extra}` : "",
      "Conserver la structure, la perspective et le mobilier existant non modifié",
      `Intensite du changement: ${intensityText}.`,
      styleHints.length ? styleHints.join(". ") + "." : ""
    ]
      .filter(Boolean)
      .join(" ");
  }

  async function ensureBaseMediaItem() {
    if (baseMedia?.mediaItemId || !baseMedia?.assetId) return null;
    try {
      const res = await fetch("/api/fast/media/item/from-asset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          asset_id: baseMedia.assetId,
          project_id: activeProjectId || null
        })
      });
      if (!res.ok) return;
      const data = await res.json();
      if (!data?.media_item_id) return;
      setBaseMedia((prev) => ({
        ...prev,
        mediaItemId: data.media_item_id,
        mediaVersionId: data.media_version_id || prev?.mediaVersionId || ""
      }));
      return { mediaItemId: data.media_item_id, mediaVersionId: data.media_version_id || "" };
    } catch {
      // ignore
    }
    return null;
  }

  async function generateVariants() {
    if (!baseImageUrl) {
      setGenerationError(t("decor.editor.err.selectBasePhoto"));
      return;
    }
    const ensured = await ensureBaseMediaItem();
    const ensuredMediaItemId = ensured?.mediaItemId || baseMedia?.mediaItemId || "";
    const ensuredMediaVersionId = ensured?.mediaVersionId || baseMedia?.mediaVersionId || "";
    setGenerating(true);
    setGenerationError("");
    let prompt = buildPrompt();
    console.log("[DecorEditor] nano/generate prompt:", prompt);

    // Merge drawing with base image when drawing is visible and has content
    let effectiveBaseUrl = baseImageUrl;
    let effectiveVersionId = ensuredMediaVersionId;
    const useDrawMerge = drawVisible && hasDrawContent();
    if (useDrawMerge) {
      try {
        const merged = await mergeDrawingWithBase();
        if (merged.url) {
          effectiveBaseUrl = merged.url;
          prompt = prompt + " Les traits dessines ne doivent plus apparaitre.";
          console.log("[DecorEditor] drawing merged, using merged image");
          // Create intermediate version (V2) for the merged drawing
          if (merged.assetId && ensuredMediaItemId) {
            try {
              const vRes = await fetch("/api/fast/media/version/from-asset", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  media_item_id: ensuredMediaItemId,
                  asset_id: merged.assetId,
                  kind: "drawing_merge",
                }),
              });
              const vData = await vRes.json().catch(() => ({}));
              if (vData.ok && vData.media_version_id) {
                effectiveVersionId = vData.media_version_id;
                console.log("[DecorEditor] created drawing merge version:", effectiveVersionId);
              }
            } catch {
              // version creation failed, continue with original version
            }
          }
        }
      } catch {
        // merge failed, continue with original base
      }
    }

    const total = Math.max(1, Math.min(6, Number(variantCount) || 1));
    setResults(
      Array.from({ length: total }, (_, idx) => ({
        id: `pending - ${idx}`,
        status: "queued",
        url: ""
      }))
    );
    track("decor_generate_started", {
      project_id: activeProjectId || "",
      variants: total,
      has_drawing: useDrawMerge
    });
    try {
      for (let i = 0; i < total; i += 1) {
        setResults((prev) =>
          prev.map((item, index) => (index === i ? { ...item, status: "generating" } : item))
        );

        // Collect texture images from selected references
        const textureImages = selectedRefs
          .filter((ref) => ref.image_url && ref.image_url.trim())
          .map((ref) => ref.image_url);

        // Build input_urls: effective base image (merged or original) + texture images
        const inputUrls = [effectiveBaseUrl, ...textureImages];

        const payload = {
          prompt,
          model_id: "models/gemini-2.5-flash-image",
          input_urls: inputUrls,
          mask_url: "",
          project_id: activeProjectId || null,
          base_media_item_id: ensuredMediaItemId || null,
          base_media_version_id: effectiveVersionId || null,
          input_media_version_ids: effectiveVersionId ? [effectiveVersionId] : [],
          prompt_initial: prompt,
          prompt_final: prompt
        };
        const jobRes = await fetch("/api/fast/jobs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ gen_type: "decor", payload })
        });
        if (!jobRes.ok) {
          const err = await jobRes.json().catch(() => ({}));
          if (jobRes.status === 429 && err.detail?.code === "TOO_MANY_CONCURRENT_JOBS") {
            setGenerationError(`Limite atteinte : ${err.detail.active_jobs}/${err.detail.limit} jobs en cours. Attendez qu'un job se termine.`);
            return;
          }
          throw new Error(err.detail || "Soumission impossible.");
        }
        const { job_id: jobId } = await jobRes.json();
        let data = null;
        for (let attempt = 0; attempt < 120; attempt++) {
          await new Promise(r => setTimeout(r, 2000));
          const poll = await (await fetch(`/api/fast/jobs/${jobId}`)).json();
          if (poll.status === "done") { data = poll.result; break; }
          if (poll.status === "error") throw new Error(poll.error || t("decor.editor.err.generationFailed"));
        }
        if (!data || !data.ok || !data.url) {
          throw new Error((data && data.error) || "Délai d'attente dépassé.");
        }
        setResults((prev) =>
          prev.map((item, index) =>
            index === i ? {
              ...item,
              status: "ready",
              url: data.url,
              mediaItemId: data.media_item_id || "",
              mediaVersionId: data.media_version_id || "",
              assetId: data.asset_id || ""
            } : item
          )
        );
      }
      track("decor_generate_success", {
        project_id: activeProjectId || "",
        variants: total
      });
      // Refresh media list so results grid can show new generated assets
      if (activeProjectId) {
        try {
          const res = await fetch("/api/fast/media/assets");
          if (res.ok) {
            const data = await res.json();
            const rawItems = Array.isArray(data.items) ? data.items : [];
            const normalized = rawItems
              .filter((entry) => entry?.url && (entry?.type || "").toLowerCase() === "image")
              .map((entry) => ({
                id: entry.media_item_id || entry.id || entry.url,
                mediaItemId: entry.media_item_id || "",
                mediaVersionId: entry.version_id || entry.current_version_id || "",
                assetId: entry.asset_id || entry.id || "",
                url: entry.url,
                thumbnailUrl: entry.thumbnail_url || "",
                projectId: entry.project_id || "",
                projectName: entry.project_name || t("decor.editor.noProject"),
                title: entry.title || "",
                createdAt: entry.created_at || ""
              }))
              .filter((entry) => (activeProjectId ? entry.projectId === activeProjectId : true))
              .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
            setMediaItems(normalized);
          }
        } catch {
          // ignore
        }
      }
    } catch (err) {
      track("decor_generate_failed", {
        project_id: activeProjectId || "",
        error: err.message || t("decor.editor.err.generation")
      });
      setGenerationError(err.message || t("decor.editor.err.generation"));
    } finally {
      setGenerating(false);
    }
  }

  async function resolveMediaEntryByUrl(url) {
    if (!url) return null;
    const existing = mediaItems.find((item) => item.url === url);
    if (existing) return existing;
    try {
      const res = await fetch("/api/fast/media/assets");
      if (!res.ok) return null;
      const data = await res.json();
      const rawItems = Array.isArray(data.items) ? data.items : [];
      const normalized = rawItems
        .filter((entry) => entry?.url && (entry?.type || "").toLowerCase() === "image")
        .map((entry) => ({
          id: entry.media_item_id || entry.id || entry.url,
          mediaItemId: entry.media_item_id || "",
          mediaVersionId: entry.version_id || entry.current_version_id || "",
          assetId: entry.asset_id || entry.id || "",
          url: entry.url,
          thumbnailUrl: entry.thumbnail_url || "",
          projectId: entry.project_id || "",
          projectName: entry.project_name || t("decor.editor.noProject"),
          title: entry.title || "",
          createdAt: entry.created_at || ""
        }));
      setMediaItems(normalized);
      return normalized.find((item) => item.url === url) || null;
    } catch {
      return null;
    }
  }

  async function keepResult(item) {
    const mediaItemId = item?.mediaItemId || "";
    const mediaVersionId = item?.mediaVersionId || "";
    let entry = item;
    if (!mediaItemId || !mediaVersionId) {
      entry = await resolveMediaEntryByUrl(item?.url);
    }
    if (!entry?.mediaItemId || !entry?.mediaVersionId) {
      setGenerationError(t("decor.editor.err.cannotResolveVersion"));
      return;
    }
    try {
      const res = await fetch("/api/fast/media/version/current", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          media_item_id: entry.mediaItemId,
          media_version_id: entry.mediaVersionId
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || t("decor.editor.err.cannotKeepVersion"));
      }
    } catch (err) {
      setGenerationError(err.message || t("decor.editor.err.updateFailed"));
    }
  }

  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem("contentos_active_project_id");
      if (stored) setActiveProjectId(stored);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    renderBase();
    renderCompare();
    renderDrawLayer();
  }, [zoom, rotation, cropRect, cropMode, cropApplied]);

  useEffect(() => {
    renderBase();
    renderCompare();
  }, [contrastValue]);

  useEffect(() => {
    renderDrawLayer();
  }, [drawVisible]);

  useEffect(() => {
    if (!cropMode) return;
    const rect = imageRectRef.current;
    if (!rect || cropRect.width || cropRect.height) return;
    const width = Math.max(rect.w * 0.7, 120);
    const height = Math.max(rect.h * 0.7, 120);
    setCropRect(
      clampCropRect({
        x: rect.x + (rect.w - width) / 2,
        y: rect.y + (rect.h - height) / 2,
        width,
        height
      })
    );
  }, [cropMode, cropRect.width, cropRect.height]);

  useEffect(() => {
    let isMounted = true;
    async function loadReferences() {
      setLoadingRefs(true);
      try {
        const res = await fetch("/api/fast/decor/references");
        if (!res.ok) throw new Error(t("decor.editor.err.loadReferences"));
        const data = await res.json();
        if (isMounted) {
          setAllReferences(data.references || []);
        }
      } catch (err) {
        console.error("[DecorEditor] Failed to load references:", err);
        if (isMounted) {
          // Fallback to empty array - default references are handled by backend
          setAllReferences([]);
        }
      } finally {
        if (isMounted) setLoadingRefs(false);
      }
    }
    loadReferences();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadActiveProject() {
      try {
        const res = await fetch("/api/fast/user/settings");
        if (!res.ok) return;
        const data = await res.json();
        if (isMounted) setActiveProjectId(data.active_project_id || "");
      } catch {
        // ignore
      }
    }
    loadActiveProject();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    async function consumePickerSelection() {
      const params = new URLSearchParams(window.location.search);
      if (params.get("media_picker_done") !== "1") return;
      if ((params.get("target") || "") !== pickerTarget) return;
      const clearSelection = async () => {
        try {
          await fetch("/api/fast/media/selection", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ image_paths: [], target: pickerTarget })
          });
        } catch {
          // ignore
        }
      };
      let handled = false;
      try {
        const raw = window.sessionStorage.getItem("contentos_media_picker_payload");
        if (raw) {
          const payload = JSON.parse(raw);
          if (payload?.target === pickerTarget && Array.isArray(payload.images)) {
            const versions = Array.isArray(payload.versions) ? payload.versions : [];
            if (versions.length) {
              const item = versions[0];
              setBaseMedia({
                id: item.mediaItemId || item.mediaVersionId || item.url,
                url: item.url,
                thumbnailUrl: item.thumbnailUrl || item.thumbnail_url || "",
                mediaItemId: item.mediaItemId || "",
                mediaVersionId: item.mediaVersionId || "",
                assetId: item.assetId || "",
                projectId: item.projectId || ""
              });
              handled = true;
            } else if (payload.images.length) {
              setBaseMedia({ id: payload.images[0], url: payload.images[0], thumbnailUrl: "" });
              handled = true;
            }
            window.sessionStorage.removeItem("contentos_media_picker_payload");
          }
        }
      } catch {
        // ignore
      }
      try {
        if (!handled) {
          const res = await fetch("/api/fast/media/selection");
          if (res.ok) {
            const data = await res.json();
            const selection = data.selection || {};
            const images = Array.isArray(selection.images) ? selection.images : [];
            if (images.length) {
              setBaseMedia({ id: images[0], url: images[0], thumbnailUrl: "" });
            }
          }
        }
      } finally {
        await clearSelection();
        params.delete("media_picker_done");
        params.delete("target");
        const base = window.location.pathname;
        const query = params.toString();
        window.history.replaceState({}, "", query ? `${base}?${query}` : base);
      }
    }
    consumePickerSelection();
  }, [pickerTarget]);

  useEffect(() => {
    let isMounted = true;
    fetchBillingStatus()
      .then((data) => {
        if (!isMounted) return;
        setCreditsRemaining(typeof data.credits_remaining === "number" ? data.credits_remaining : null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setBillingError(err.message || t("decor.editor.err.billing"));
        setCreditsRemaining(null);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadMedia() {
      setLoadingMedia(true);
      setMediaError("");
      try {
        const res = await fetch("/api/fast/media/assets");
        if (!res.ok) throw new Error(t("decor.editor.err.loadMedia"));
        const data = await res.json();
        const rawItems = Array.isArray(data.items) ? data.items : [];
        const normalized = rawItems
          .filter((entry) => entry?.url && (entry?.type || "").toLowerCase() === "image")
          .map((entry) => ({
            id: entry.media_item_id || entry.id || entry.url,
            mediaItemId: entry.media_item_id || "",
            mediaVersionId: entry.version_id || entry.current_version_id || "",
            assetId: entry.asset_id || entry.id || "",
            url: entry.url,
            thumbnailUrl: entry.thumbnail_url || "",
            projectId: entry.project_id || "",
            projectName: entry.project_name || t("decor.editor.noProject"),
            title: entry.title || "",
            createdAt: entry.created_at || ""
          }))
          .filter((entry) => (activeProjectId ? entry.projectId === activeProjectId : true))
          .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
        if (!isMounted) return;
        setMediaItems(normalized);
        if (normalized.length) {
          setBaseMedia((prev) => prev || normalized[0]);
        }
      } catch (err) {
        if (!isMounted) return;
        setMediaItems([]);
        setMediaError(err.message || t("decor.editor.err.unknown"));
      } finally {
        if (isMounted) setLoadingMedia(false);
      }
    }
    loadMedia();
    return () => {
      isMounted = false;
    };
  }, [activeProjectId]);

  useEffect(() => {
    let isMounted = true;
    if (!baseImageUrl) {
      imageRef.current = null;
      imageRectRef.current = null;
      setRotation(0);
      setContrastValue(100);
      setCropMode(false);
      setCropApplied(false);
      setCropRect({ x: 0, y: 0, width: 0, height: 0 });
      renderBase();
      renderCompare();

      return () => {
        isMounted = false;
      };
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!isMounted) return;
      imageRef.current = img;
      drawBufferRef.current = null;
      setRotation(0);
      setContrastValue(100);
      setCropMode(false);
      setCropApplied(false);
      setCropRect({ x: 0, y: 0, width: 0, height: 0 });
      renderBase();
      renderCompare();

      renderDrawLayer();
    };
    img.onerror = () => {
      if (!isMounted) return;
      imageRef.current = null;
      imageRectRef.current = null;
      setRotation(0);
      setContrastValue(100);
      setCropMode(false);
      setCropApplied(false);
      setCropRect({ x: 0, y: 0, width: 0, height: 0 });
      renderBase();
      renderCompare();

    };
    img.src = toProxyUrl(baseImageUrl);
    return () => {
      isMounted = false;
    };
  }, [baseImageUrl]);

  useEffect(() => {
    let isMounted = true;
    if (!compareImageUrl) {
      compareImageRef.current = null;
      renderCompare();
      return () => {
        isMounted = false;
      };
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (!isMounted) return;
      compareImageRef.current = img;
      renderCompare();
    };
    img.onerror = () => {
      if (!isMounted) return;
      compareImageRef.current = null;
      renderCompare();
    };
    img.src = toProxyUrl(compareImageUrl);
    return () => {
      isMounted = false;
    };
  }, [compareImageUrl]);

  useEffect(() => {
    const wrapper = canvasWrapRef.current;
    if (!wrapper) return () => { };
    const resize = () => {
      resizeCanvases();
      renderBase();
      renderCompare();

      renderDrawLayer();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(wrapper);
    window.addEventListener("resize", resize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  async function uploadBaseFiles(files = []) {
    if (!files.length) return;
    setUploading(true);
    setUploadError("");
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      const res = await fetch("/api/fast/media/uploads", {
        method: "POST",
        body: formData
      });
      if (!res.ok) throw new Error(t("decor.editor.err.uploadFailed"));
      const data = await res.json();
      const newItems = Array.isArray(data.items) ? data.items : [];
      if (!newItems.length) throw new Error(t("decor.editor.err.uploadFailed"));
      const images = newItems
        .filter((item) => (item.type || "").toLowerCase() === "image")
        .map((item) => ({
          id: item.media_item_id || item.id || item.url,
          mediaItemId: item.media_item_id || "",
          mediaVersionId: item.version_id || item.current_version_id || "",
          assetId: item.asset_id || item.id || "",
          url: item.url,
          thumbnailUrl: item.thumbnail_url || "",
          projectId: item.project_id || "",
          projectName: item.project_name || t("decor.editor.noProject"),
          title: item.title || "",
          createdAt: item.created_at || ""
        }));
      if (images.length) {
        setMediaItems((prev) => [...images, ...prev]);
        setBaseMedia(images[0]);
      }
    } catch (err) {
      setUploadError(err.message || t("decor.editor.err.uploadFailed"));
    } finally {
      setUploading(false);
    }
  }

  async function handleBaseUpload(event) {
    const files = Array.from(event.target.files || []);
    await uploadBaseFiles(files);
    event.target.value = "";
  }

  async function handleDrop(event) {
    event.preventDefault();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer?.files || []).filter(
      (file) => (file.type || "").startsWith("image/")
    );
    if (!files.length) return;
    await uploadBaseFiles(files);
  }

  async function handleReferenceImageUpload(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setRefImageUploading(true);
    setRefImageError("");
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      const res = await fetch("/api/fast/media/uploads", {
        method: "POST",
        body: formData
      });
      if (!res.ok) throw new Error(t("decor.editor.err.uploadFailed"));
      const data = await res.json();
      const newItems = Array.isArray(data.items) ? data.items : [];
      const image = newItems.find((item) => (item.type || "").toLowerCase() === "image");
      const url = image?.url || image?.preview_url || image?.thumbnail_url || "";
      if (!url) throw new Error(t("decor.editor.err.noImageAvailable"));
      setRefFormData((prev) => ({ ...prev, image_url: url }));
    } catch (err) {
      setRefImageError(err.message || t("decor.editor.err.uploadFailed"));
    } finally {
      setRefImageUploading(false);
      event.target.value = "";
    }
  }

  async function handleReferenceDrop(event) {
    event.preventDefault();
    setIsRefDragging(false);
    const files = Array.from(event.dataTransfer?.files || []).filter(
      (file) => (file.type || "").startsWith("image/")
    );
    if (!files.length) return;
    setRefDropUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      const res = await fetch("/api/fast/media/uploads", {
        method: "POST",
        body: formData
      });
      if (!res.ok) throw new Error(t("decor.editor.err.uploadFailed"));
      const data = await res.json();
      const newItems = Array.isArray(data.items) ? data.items : [];
      const image = newItems.find((item) => (item.type || "").toLowerCase() === "image");
      const url = image?.url || image?.preview_url || image?.thumbnail_url || "";
      if (!url) throw new Error(t("decor.editor.err.noImageAvailable"));
      openCreateRefModal({ image_url: url });
    } catch {
      // ignore
    } finally {
      setRefDropUploading(false);
    }
  }

  useEffect(() => {
    let isMounted = true;
    async function loadVersions() {
      if (!baseMedia?.mediaItemId && !baseMedia?.assetId) {
        setVersionItems([]);
        return;
      }
      setVersionsLoading(true);
      try {
        const query = baseMedia.mediaItemId
          ? `media_item_id=${encodeURIComponent(baseMedia.mediaItemId)}`
          : `asset_id=${encodeURIComponent(baseMedia.assetId)}`;
        const res = await fetch(`/api/fast/media/detail?${query}`);
        if (!res.ok) throw new Error(t("decor.editor.err.loadVersions"));
        const data = await res.json();
        if (!isMounted) return;
        const versions = Array.isArray(data?.versions) ? data.versions : [];
        // API returns newest-first; display oldest-first (V1 = base image)
        setVersionItems([...versions].reverse());
      } catch {
        if (!isMounted) return;
        setVersionItems([]);
      } finally {
        if (isMounted) setVersionsLoading(false);
      }
    }
    loadVersions();
    return () => {
      isMounted = false;
    };
  }, [baseMedia?.mediaItemId, baseMedia?.assetId]);

  function toggleRef(item) {
    setRefWarning("");
    setSelectedRefs((prev) => {
      const exists = prev.find((ref) => ref.id === item.id);
      if (exists) return prev.filter((ref) => ref.id !== item.id);
      if (prev.length >= maxRefs) {
        setRefWarning(`${t("decor.editor.warn.refLimit")} (${maxRefs}).`);
        return prev;
      }
      return [...prev, item];
    });
  }

  function openCreateRefModal(overrides = {}) {
    const type = overrides.type || (refTab === "palettes" ? "palette" : refTab === "textures" ? "texture" : "furniture");
    setRefFormData({
      name: "",
      type,
      colors: type === "palette" ? ["#FFFFFF", "#000000"] : [],
      tone: type !== "palette" ? ["#CCCCCC", "#999999"] : [],
      tag: "",
      image_url: overrides.image_url || "",
      description: ""
    });
    setRefModalMode("create");
    setEditingRef(null);
    setShowRefModal(true);
  }

  function openEditRefModal(ref) {
    setRefFormData({
      name: ref.name || "",
      type: ref.type || "palette",
      colors: ref.colors || [],
      tone: ref.tone || [],
      tag: ref.tag || "",
      image_url: ref.image_url || "",
      description: ref.description || ""
    });
    setRefModalMode("edit");
    setEditingRef(ref);
    setShowRefModal(true);
  }

  async function handleSaveReference() {
    if (!refFormData.name.trim()) {
      setGenerationError(t("decor.editor.err.nameRequired"));
      return;
    }

    try {
      if (refModalMode === "create") {
        const res = await fetch("/api/fast/decor/references", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(refFormData)
        });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error || t("decor.editor.err.createReference"));
        setAllReferences((prev) => [...prev, data.reference]);
      } else {
        const res = await fetch("/api/fast/decor/references", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reference_id: editingRef.id || editingRef.reference_id,
            ...refFormData
          })
        });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error || t("decor.editor.err.updateReference"));
        setAllReferences((prev) =>
          prev.map((r) => (r.id === data.reference.id ? data.reference : r))
        );
      }
      setShowRefModal(false);
    } catch (err) {
      setGenerationError(err.message || t("decor.editor.err.saveReference"));
    }
  }

  async function handleDeleteReference(ref) {
    if (!confirm(`${t("decor.editor.confirm.deleteReference")} "${ref.name}" ?`)) return;

    try {
      const res = await fetch("/api/fast/decor/references", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference_ids: [ref.id || ref.reference_id] })
      });
      const data = await res.json();
      if (!data.ok) throw new Error(t("decor.editor.err.deleteReference"));

      setAllReferences((prev) => prev.filter((r) => r.id !== ref.id && r.reference_id !== ref.id));
      setSelectedRefs((prev) => prev.filter((r) => r.id !== ref.id));
    } catch (err) {
      setGenerationError(err.message || t("decor.editor.err.deleteReference"));
    }
  }

  return (
    <div className="app-shell">
      <Sidebar active="decor" />
      <div className="app-main app-main-full">
        <div className="decor-page">
          <header className="decor-header">
            <div>
              <p className="decor-kicker">{t("decor.editor.kicker")}</p>
              <h1 className="decor-title page-title-accent">{t("decor.editor.title")}</h1>
              <p className="decor-subtitle">
                {t("decor.editor.subtitle")}
              </p>
            </div>
            <div className="decor-header-actions">
              <button className="decor-pill" type="button" onClick={() => window.location.assign("/projects")}>
                <span className="material-symbols-rounded">arrow_back</span>
                {t("decor.editor.backProjects")}
              </button>
              <button
                className="decor-pill accent"
                type="button"
                onClick={() => startPortal().catch(() => { })}
              >
                {creditsLabel}
              </button>
            </div>
          </header>

          {billingError && <div className="nano-alert error">{billingError}</div>}
          {mediaError && <div className="nano-alert error">{mediaError}</div>}
          {uploadError && <div className="nano-alert error">{uploadError}</div>}
          {generationError && <div className="nano-alert error">{generationError}</div>}
          {editError && <div className="nano-alert error">{editError}</div>}

          <div className="decor-layout">
            <main className="decor-canvas-area">
              <div className="decor-canvas-toolbar">
                <div className="decor-toolgroup">
                  <button
                    className="decor-tool"
                    type="button"
                    onClick={handleZoomOut}
                    disabled={!baseImageUrl || zoom <= 0.5}
                    title={t("decor.editor.zoomOut")}
                  >
                    <span className="material-symbols-rounded">zoom_out</span>
                  </button>
                  <button
                    className="decor-tool"
                    type="button"
                    onClick={handleZoomIn}
                    disabled={!baseImageUrl || zoom >= 3}
                    title={t("decor.editor.zoomIn")}
                  >
                    <span className="material-symbols-rounded">zoom_in</span>
                  </button>
                  <button
                    className="decor-tool"
                    type="button"
                    onClick={handleFit}
                    disabled={!baseImageUrl || zoom === 1}
                    title={t("decor.editor.fit")}
                  >
                    <span className="material-symbols-rounded">fit_screen</span>
                    {t("decor.editor.fit")}
                  </button>
                </div>
                <div className="decor-toolgroup">
                  <button
                    className="decor-tool"
                    type="button"
                    onClick={handleRotateCounterClockwise}
                    disabled={!baseImageUrl}
                    title={t("decor.editor.rotateLeft")}
                  >
                    <span className="material-symbols-rounded">rotate_left</span>
                  </button>
                  <button
                    className="decor-tool"
                    type="button"
                    onClick={handleRotateClockwise}
                    disabled={!baseImageUrl}
                    title={t("decor.editor.rotateRight")}
                  >
                    <span className="material-symbols-rounded">rotate_right</span>
                  </button>
                  <button
                    className="decor-tool"
                    type="button"
                    onClick={() => setRotation(0)}
                    disabled={!baseImageUrl || rotation === 0}
                    title={t("decor.editor.resetRotation")}
                  >
                    <span className="material-symbols-rounded">restart_alt</span>
                  </button>
                </div>
                <div className="decor-toolgroup">
                  <label className="decor-contrast">
                    {t("decor.editor.contrast")}
                    <input
                      type="range"
                      min="60"
                      max="140"
                      value={contrastValue}
                      onChange={(event) => setContrastValue(Number(event.target.value))}
                      disabled={!baseImageUrl}
                    />
                  </label>
                </div>
                <div className="decor-toolgroup">
                  <button
                    className={`decor-tool ${cropMode ? "active" : ""}`}
                    type="button"
                    onClick={() => {
                      if (cropMode) {
                        setCropApplied(Boolean(cropRect.width && cropRect.height));
                        setCropMode(false);
                        return;
                      }
                      setCropApplied(false);
                      setCropMode(true);
                    }}
                    disabled={!baseImageUrl}
                    title={t("decor.editor.crop")}
                  >
                    <span className="material-symbols-rounded">crop</span>
                    {cropMode ? t("common.cancel") : t("decor.editor.crop")}
                  </button>
                  <button
                    className="decor-tool"
                    type="button"
                    onClick={handleSaveEdits}
                    disabled={!baseMedia || editSaving || !hasEditChanges}
                  >
                    <span className="material-symbols-rounded">done</span>
                    {editSaving ? t("decor.editor.saving") : t("decor.editor.validate")}
                  </button>
                </div>
              </div>
              <div className="decor-canvas-stage">
                <div
                  className={`decor-canvas-placeholder ${isDragging ? "is-dragging" : ""}`}
                  ref={canvasWrapRef}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  {!baseImageUrl && (
                    <div className="decor-dropzone">
                      <div className="decor-dropzone-inner">
                        <div className="decor-dropzone-title">
                          {loadingMedia ? t("common.loading") : t("decor.editor.dropImage")}
                        </div>
                        <div className="decor-dropzone-subtitle">
                          {t("decor.editor.dropImageSubtitle")}
                        </div>
                        <label className={`decor-dropzone-btn ${uploading ? "is-loading" : ""}`}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleBaseUpload}
                            disabled={uploading}
                            hidden
                          />
                          {uploading ? t("decor.editor.uploading") : t("decor.editor.uploadImage")}
                        </label>
                        {uploadError && <div className="decor-dropzone-error">{uploadError}</div>}
                      </div>
                    </div>
                  )}
                  <canvas ref={baseCanvasRef} className="decor-canvas" aria-hidden />
                  <canvas
                    ref={compareCanvasRef}
                    className="decor-canvas decor-canvas-compare"
                    style={{
                      display: compareEnabled && compareImageUrl ? "block" : "none",
                      clipPath: `inset(0 0 0 ${100 - compareValue}%)`
                    }}
                    aria-hidden
                  />
                  {drawMode && (
                    <canvas
                      ref={drawCanvasRef}
                      className="decor-canvas decor-canvas-draw"
                      style={{ display: drawVisible ? "block" : "none" }}
                      onPointerDown={(event) => {
                        if (!baseImageUrl) return;
                        event.preventDefault();
                        event.currentTarget.setPointerCapture(event.pointerId);
                        isDrawingRef.current = true;
                        const point = getPointerPos(event);
                        setCursorPos({ x: point?.x || 0, y: point?.y || 0, visible: true });
                        lastPointRef.current = point;
                        drawAtPoint(point, true);
                      }}
                      onPointerMove={(event) => {
                        const point = getPointerPos(event);
                        if (!point) return;
                        const rect = imageRectRef.current;
                        const inside =
                          rect &&
                          point.x >= rect.x &&
                          point.y >= rect.y &&
                          point.x <= rect.x + rect.w &&
                          point.y <= rect.y + rect.h;
                        setCursorPos({ x: point.x, y: point.y, visible: Boolean(inside) });
                        if (!isDrawingRef.current) return;
                        drawAtPoint(point, false);
                        lastPointRef.current = point;
                      }}
                      onPointerUp={(event) => {
                        event.currentTarget.releasePointerCapture(event.pointerId);
                        isDrawingRef.current = false;
                        lastPointRef.current = null;
                      }}
                      onPointerLeave={() => {
                        isDrawingRef.current = false;
                        lastPointRef.current = null;
                        setCursorPos((prev) => ({ ...prev, visible: false }));
                      }}
                    />
                  )}
                  {cropMode && (
                    <div
                      className="media-edit-crop"
                      style={{
                        left: cropRect.x,
                        top: cropRect.y,
                        width: cropRect.width,
                        height: cropRect.height
                      }}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        cropDragRef.current = {
                          handle: "move",
                          startX: event.clientX,
                          startY: event.clientY,
                          rect: { ...cropRect }
                        };
                        const onMove = (moveEvent) => {
                          const payload = cropDragRef.current;
                          if (!payload) return;
                          const dx = moveEvent.clientX - payload.startX;
                          const dy = moveEvent.clientY - payload.startY;
                          setCropRect(
                            clampCropRect({
                              ...payload.rect,
                              x: payload.rect.x + dx,
                              y: payload.rect.y + dy
                            })
                          );
                        };
                        const onUp = () => {
                          cropDragRef.current = null;
                          window.removeEventListener("pointermove", onMove);
                          window.removeEventListener("pointerup", onUp);
                        };
                        window.addEventListener("pointermove", onMove);
                        window.addEventListener("pointerup", onUp);
                      }}
                    >
                      {["nw", "ne", "sw", "se"].map((handle) => (
                        <button
                          key={handle}
                          type="button"
                          className={`media-edit-handle ${handle}`}
                          onPointerDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            cropDragRef.current = {
                              handle,
                              startX: event.clientX,
                              startY: event.clientY,
                              rect: { ...cropRect }
                            };
                            const onMove = (moveEvent) => {
                              const payload = cropDragRef.current;
                              if (!payload) return;
                              const dx = moveEvent.clientX - payload.startX;
                              const dy = moveEvent.clientY - payload.startY;
                              const minSize = 40;
                              let { x, y, width, height } = payload.rect;
                              if (payload.handle.includes("n")) {
                                y = y + dy;
                                height = payload.rect.height - dy;
                              }
                              if (payload.handle.includes("s")) {
                                height = payload.rect.height + dy;
                              }
                              if (payload.handle.includes("w")) {
                                x = x + dx;
                                width = payload.rect.width - dx;
                              }
                              if (payload.handle.includes("e")) {
                                width = payload.rect.width + dx;
                              }
                              if (width < minSize || height < minSize) return;
                              setCropRect(clampCropRect({ x, y, width, height }));
                            };
                            const onUp = () => {
                              cropDragRef.current = null;
                              window.removeEventListener("pointermove", onMove);
                              window.removeEventListener("pointerup", onUp);
                            };
                            window.addEventListener("pointermove", onMove);
                            window.addEventListener("pointerup", onUp);
                          }}
                          aria-label={t("decor.editor.resizeFrame")}
                        />
                      ))}
                    </div>
                  )}
                  <div
                    className="decor-cursor"
                    style={{
                      width: drawBrushSize,
                      height: drawBrushSize,
                      left: cursorPos.x,
                      top: cursorPos.y,
                      opacity: cursorPos.visible && drawMode ? 1 : 0
                    }}
                  />
                  {compareEnabled && compareImageUrl && (
                    <div className="decor-compare">
                      <span>{t("decor.editor.before")}</span>
                      <div className="decor-compare-track">
                        <div
                          className="decor-compare-handle"
                          style={{ left: `${compareValue}%` }}
                        />
                      </div>
                      <span>{t("decor.editor.after")}</span>
                      <input
                        className="decor-compare-range"
                        type="range"
                        min="0"
                        max="100"
                        value={compareValue}
                        onChange={(event) => setCompareValue(Number(event.target.value))}
                      />
                      <button
                        type="button"
                        className="decor-compare-close"
                        onClick={() => setCompareEnabled(false)}
                      >
                        {t("common.close")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </main>

            <aside className="decor-controls">
              <section className="decor-panel">
                <div className="decor-panel-header">
                  <h2>{t("decor.editor.basePhoto")}</h2>
                </div>
                <div className="decor-base-media">
                  {baseMedia ? (
                    <img
                      src={baseMedia.thumbnailUrl || baseMedia.url}
                      alt={t("decor.editor.baseSelection")}
                      className="decor-base-thumb"
                    />
                  ) : (
                    <div className="decor-base-empty">{t("decor.editor.noSelection")}</div>
                  )}
                  <div className="decor-base-actions">
                    <a className="decor-secondary" href={pickerUrl}>
                      {t("mediasel.library")}
                    </a>
                    <label className={`decor-secondary decor-upload ${uploading ? "is-loading" : ""}`}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBaseUpload}
                        disabled={uploading}
                      />
                      {uploading ? t("decor.editor.uploading") : t("segment.upload")}
                    </label>
                    {baseMedia && (
                      <button
                        type="button"
                        className="decor-secondary"
                        onClick={() => setBaseMedia(null)}
                      >
                        {t("decor.editor.remove")}
                      </button>
                    )}
                  </div>
                </div>
              </section>
              <section className="decor-panel">
                <div className="decor-panel-header">
                  <h2>{t("decor.editor.drawing")}</h2>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <button
                      type="button"
                      className={`decor-chip${drawVisible ? " active" : ""}`}
                      onClick={() => {
                        setDrawVisible((v) => !v);
                        requestAnimationFrame(renderDrawLayer);
                      }}
                      title={drawVisible ? t("decor.editor.hideDrawing") : t("decor.editor.showDrawing")}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: 16 }}>
                        {drawVisible ? "visibility" : "visibility_off"}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`decor-chip${drawMode ? " active" : ""}`}
                      onClick={() => setDrawMode((v) => !v)}
                    >
                      <span className="material-symbols-rounded" style={{ fontSize: 16 }}>draw</span>
                      {drawMode ? t("decor.editor.active") : t("decor.editor.draw")}
                    </button>
                  </div>
                </div>
                {drawMode && (
                  <>
                    <div className="decor-draw-swatches">
                      {["#FF0000", "#3B82F6", "#22C55E", "#FACC15", "#FFFFFF", "#000000"].map((c) => (
                        <button
                          key={c}
                          type="button"
                          className={`decor-draw-swatch${drawColor === c ? " active" : ""}`}
                          style={{ background: c }}
                          onClick={() => setDrawColor(c)}
                          aria-label={c}
                        />
                      ))}
                      <input
                        type="color"
                        className="decor-draw-color-input"
                        value={drawColor}
                        onChange={(e) => setDrawColor(e.target.value)}
                        title={t("decor.editor.customColor")}
                      />
                    </div>
                    <label className="decor-slider">
                      {t("decor.editor.brushSize")}
                      <input
                        type="range"
                        min="1"
                        max="100"
                        value={drawBrushSize}
                        onChange={(event) => setDrawBrushSize(Number(event.target.value))}
                      />
                      <span>{drawBrushSize}px</span>
                    </label>
                    <div className="decor-panel-actions">
                      <button className="decor-secondary" type="button" onClick={clearDrawing}>
                        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>delete</span>
                        {t("decor.editor.clear")}
                      </button>
                    </div>
                  </>
                )}
              </section>

              <section className="decor-panel">
                <div className="decor-panel-header">
                  <h2>{t("decor.editor.references")}</h2>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span className="decor-muted">{selectedRefs.length}/{maxRefs}</span>
                    <button
                      type="button"
                      className="decor-add-ref-btn"
                      onClick={openCreateRefModal}
                      title={t("decor.editor.addReference")}
                    >
                      + {t("decor.editor.add")}
                    </button>
                  </div>
                </div>
                <div
                  className={`decor-ref-dropzone ${isRefDragging ? "is-dragging" : ""}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsRefDragging(true);
                  }}
                  onDragLeave={() => setIsRefDragging(false)}
                  onDrop={handleReferenceDrop}
                >
                  <div>
                    <strong>{t("decor.editor.dropRefImage")}</strong>
                    <span>{t("decor.editor.dropRefImageSubtitle")}</span>
                  </div>
                  <label className={`decor-secondary decor-upload ${refDropUploading ? "is-loading" : ""}`}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleReferenceImageUpload}
                      disabled={refDropUploading}
                    />
                    {refDropUploading ? t("decor.editor.uploading") : t("decor.editor.uploadImage")}
                  </label>
                  {refDropUploading && <span className="decor-muted">{t("decor.editor.uploading")}</span>}
                </div>
                <div className="decor-tabs">
                  <button
                    type="button"
                    className={refTab === "palettes" ? "active" : ""}
                    onClick={() => setRefTab("palettes")}
                  >
                    {t("decor.editor.palettes")}
                  </button>
                  <button
                    type="button"
                    className={refTab === "textures" ? "active" : ""}
                    onClick={() => setRefTab("textures")}
                  >
                    {t("decor.editor.textures")}
                  </button>
                  <button
                    type="button"
                    className={refTab === "furniture" ? "active" : ""}
                    onClick={() => setRefTab("furniture")}
                  >
                    {t("decor.editor.furniture")}
                  </button>
                </div>
                <div className="decor-ref-grid">
                  {loadingRefs && <p className="decor-muted">{t("common.loading")}</p>}
                  {!loadingRefs && currentRefs.map((item) => {
                    const selected = selectedRefs.some((ref) => ref.id === item.id);
                    const isCustom = !item.is_default;
                    return (
                      <div key={item.id} className="decor-ref-card-wrapper">
                        <button
                          type="button"
                          className={`decor-ref-card ${selected ? "selected" : ""}`}
                          onClick={() => toggleRef(item)}
                        >
                          <div
                            className="decor-ref-swatch"
                            style={{
                              background: item.image_url
                                ? `url(${item.image_url}) center / cover no-repeat`
                                : `linear-gradient(135deg, ${item.tone?.[0] || item.colors[0]}, ${item.tone?.[1] || item.colors[item.colors.length - 1]})`
                            }}
                          />
                          <div className="decor-ref-meta">
                            <strong>{item.name}</strong>
                            <span>{item.tag || t("decor.editor.palette")}</span>
                          </div>
                        </button>
                        {isCustom && (
                          <div className="decor-ref-menu">
                            <button
                              type="button"
                              className="decor-ref-menu-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditRefModal(item);
                              }}
                              title={t("decor.editor.edit")}
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              className="decor-ref-menu-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteReference(item);
                              }}
                              title={t("common.delete")}
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {refWarning && <div className="decor-warning">{refWarning}</div>}
                <div className="decor-selected">
                  {selectedRefs.length === 0 && <p className="decor-muted">{t("decor.editor.noReference")}</p>}
                  {selectedRefs.map((ref) => (
                    <div key={ref.id} className="decor-selected-item">
                      <span>{ref.name}</span>
                      <button type="button" onClick={() => toggleRef(ref)}>
                        <span className="material-symbols-rounded">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <section className="decor-panel">
                <div className="decor-panel-header">
                  <h2>{t("decor.editor.parameters")}</h2>
                </div>
                <label className="decor-textarea">
                  {t("decor.editor.extraNotes")}
                  <textarea
                    maxLength={2000}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder={t("decor.editor.notesPlaceholder")}
                  />
                </label>
                <label className="decor-slider">
                  {t("decor.editor.variants")}
                  <input
                    type="range"
                    min="1"
                    max="6"
                    value={variantCount}
                    onChange={(event) => setVariantCount(Number(event.target.value))}
                  />
                  <span>{variantCount}</span>
                </label>
                <label className="decor-slider">
                  {t("decor.editor.intensity")}
                  <input
                    type="range"
                    min="20"
                    max="100"
                    value={intensity}
                    onChange={(event) => setIntensity(Number(event.target.value))}
                  />
                  <span>{intensity}%</span>
                </label>
                <button className="decor-generate" type="button" disabled={!canGenerate} onClick={generateVariants}>
                  {generating ? t("decor.editor.generating") : t("decor.editor.generate")}
                </button>
                <p className="decor-cost">{t("decor.editor.cost")} {estimatedCost} {t("pricing.credits")}</p>
              </section>

              <section className="decor-panel">
                <div className="decor-panel-header">
                  <h2>{t("decor.editor.history")}</h2>
                </div>
                <div className="decor-history">
                  {versionsLoading && <p className="decor-muted">{t("common.loading")}</p>}
                  {!versionsLoading && versionItems.length === 0 && (
                    <p className="decor-muted">{t("decor.editor.noVersion")}</p>
                  )}
                  {!versionsLoading &&
                    versionItems.map((entry, index) => {
                      const version = entry?.version || {};
                      const asset = entry?.asset || {};
                      const thumb = asset.thumbnail_url || asset.url || "";
                      const label = version.label || `${t("decor.editor.version")} ${index + 1}`;
                      const time = version.created_at || version.updated_at || "";
                      return (
                        <button
                          key={version.media_version_id || index}
                          type="button"
                          className="decor-history-item"
                          onClick={() =>
                            setBaseMedia((prev) => ({
                              ...prev,
                              id: asset.id || version.media_version_id || prev?.id || "",
                              mediaItemId: version.media_item_id || prev?.mediaItemId || "",
                              mediaVersionId: version.media_version_id || prev?.mediaVersionId || "",
                              assetId: asset.id || prev?.assetId || "",
                              url: asset.url || prev?.url || "",
                              thumbnailUrl: asset.thumbnail_url || prev?.thumbnailUrl || "",
                              projectId: asset.project_id || prev?.projectId || "",
                              projectName: asset.project_name || prev?.projectName || ""
                            }))
                          }
                        >
                          <div
                            className="decor-history-thumb"
                            style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
                          />
                          <div>
                            <strong>{label}</strong>
                            <span>{time || "—"}</span>
                          </div>
                          <span className="material-symbols-rounded">more_vert</span>
                        </button>
                      );
                    })}
                </div>
              </section>
            </aside>
          </div>

          <section className="decor-results">
            <div className="decor-results-header">
              <div>
                <h2>{t("decor.editor.generatedVariants")}</h2>
                <p>
                  {(results.length ? results.filter((r) => r.status === "ready").length : displayedResults.length)}/
                  {variantCount} {t("decor.editor.variantsGenerated")}
                </p>
              </div>
              <button className="decor-secondary" type="button" onClick={generateVariants} disabled={!canGenerate}>
                {generating ? t("decor.editor.generating") : t("decor.editor.regenerate")}
              </button>
            </div>
            <div className="decor-results-grid">
              {(results.length ? results : displayedResults).map((item, index) => {
                const isGenerated = results.length > 0;
                const thumb = isGenerated ? item.url : item.thumbnailUrl || item.url;
                const fullUrl = item.url || thumb;
                const status = isGenerated ? item.status : "ready";
                return (
                  <div key={item.id || `${item.url} -${index} `} className={`decor-result-card ${status}`}>
                    <div className="decor-result-thumb-wrap">
                      {thumb
                        ? <img src={thumb} className="decor-result-thumb" alt="" />
                        : <div className="decor-result-thumb decor-result-thumb--empty" />
                      }
                      {status === "ready" && fullUrl && (
                        <button
                          type="button"
                          className="decor-zoom-btn"
                          onClick={() => setZoomItem({ url: fullUrl })}
                          aria-label="Zoom"
                        >
                          <span className="material-symbols-rounded">zoom_in</span>
                        </button>
                      )}
                    </div>
                    <div className="decor-result-status">
                      {status === "ready" && t("decor.editor.ready")}
                      {status === "generating" && t("decor.editor.generating")}
                      {status === "queued" && t("decor.editor.queued")}
                    </div>
                    <div className="decor-result-actions">
                      <button
                        type="button"
                        onClick={() => {
                          if (isGenerated && item.url) {
                            setBaseMedia({
                              id: item.mediaItemId || item.id,
                              url: item.url,
                              thumbnailUrl: item.url,
                              mediaItemId: item.mediaItemId || "",
                              mediaVersionId: item.mediaVersionId || "",
                              assetId: item.assetId || ""
                            });
                          } else {
                            setBaseMedia(item);
                          }
                        }}
                      >
                        {t("decor.editor.open")}
                      </button>
                      {fullUrl ? (
                        <button
                          type="button"
                          className="decor-action-link"
                          onClick={() => downloadFile(fullUrl, buildDownloadName(fullUrl))}
                        >
                          {t("decor.editor.download")}
                        </button>
                      ) : (
                        <button type="button" disabled>
                          {t("decor.editor.download")}
                        </button>
                      )}
                      <button type="button" onClick={() => keepResult(isGenerated ? { ...item, url: fullUrl } : item)}>
                        {t("decor.editor.keep")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!fullUrl) return;
                          setCompareImageUrl(fullUrl);
                          setCompareValue(50);
                          setCompareEnabled(true);
                        }}
                      >
                        {t("decor.editor.compare")}
                      </button>
                    </div>
                  </div>
                );
              })}
              {!results.length && !displayedResults.length && !loadingMedia && (
                <div className="decor-result-card ready">
                  <div className="decor-result-thumb" />
                  <div className="decor-result-status">{t("decor.editor.noResult")}</div>
                </div>
              )}
            </div>
            <div className="decor-progress">
              <div
                className="decor-progress-bar"
                style={{
                  width: `${variantCount ? ((results.length
                    ? results.filter((r) => r.status === "ready").length
                    : displayedResults.length) / variantCount) * 100 : 0
                    }% `
                }}
              />
            </div>
          </section>
        </div>
      </div>

      {/* Reference Modal */}
      {showRefModal && (
        <div className="decor-modal-overlay" onClick={() => setShowRefModal(false)}>
          <div className="decor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="decor-modal-header">
              <h3>{refModalMode === "create" ? t("decor.editor.addReference") : t("decor.editor.editReference")}</h3>
              <button type="button" onClick={() => setShowRefModal(false)}>×</button>
            </div>
            <div className="decor-modal-body">
              <label>
                <span>{t("decor.editor.name")} *</span>
                <input
                  type="text"
                  value={refFormData.name}
                  onChange={(e) => setRefFormData({ ...refFormData, name: e.target.value })}
                  placeholder={t("decor.editor.namePlaceholder")}
                />
              </label>

              {refFormData.type === "palette" && (
                <label>
                  <span>{t("decor.editor.colors")}</span>
                  <div className="decor-color-picker-grid">
                    {refFormData.colors.map((color, idx) => (
                      <div key={idx} className="decor-color-picker-item">
                        <input
                          type="color"
                          value={color}
                          onChange={(e) => {
                            const updated = [...refFormData.colors];
                            updated[idx] = e.target.value;
                            setRefFormData({ ...refFormData, colors: updated });
                          }}
                        />
                        {refFormData.colors.length > 2 && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = refFormData.colors.filter((_, i) => i !== idx);
                              setRefFormData({ ...refFormData, colors: updated });
                            }}
                            title={t("common.delete")}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    {refFormData.colors.length < 6 && (
                      <button
                        type="button"
                        className="decor-add-color-btn"
                        onClick={() => {
                          setRefFormData({
                            ...refFormData,
                            colors: [...refFormData.colors, "#CCCCCC"]
                          });
                        }}
                      >
                        + {t("decor.editor.color")}
                      </button>
                    )}
                  </div>
                </label>
              )}

              {refFormData.type !== "palette" && (
                <>
                  <label>
                    <span>{t("decor.editor.tones")}</span>
                    <div className="decor-color-picker-grid">
                      {refFormData.tone.map((color, idx) => (
                        <input
                          key={idx}
                          type="color"
                          value={color}
                          onChange={(e) => {
                            const updated = [...refFormData.tone];
                            updated[idx] = e.target.value;
                            setRefFormData({ ...refFormData, tone: updated });
                          }}
                        />
                      ))}
                    </div>
                  </label>

                  <label>
                    <span>{t("decor.editor.imageUrlOptional")}</span>
                    <input
                      type="text"
                      value={refFormData.image_url}
                      onChange={(e) => setRefFormData({ ...refFormData, image_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </label>
                  <div className="decor-ref-upload">
                    <label className={`decor-secondary decor-upload ${refImageUploading ? "is-loading" : ""}`}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleReferenceImageUpload}
                        disabled={refImageUploading}
                        hidden
                      />
                      {refImageUploading ? t("decor.editor.uploading") : t("decor.editor.uploadImage")}
                    </label>
                    {refImageError && <span className="decor-ref-upload-error">{refImageError}</span>}
                  </div>

                  <label>
                    <span>{t("decor.editor.tagOptional")}</span>
                    <input
                      type="text"
                      value={refFormData.tag}
                      onChange={(e) => setRefFormData({ ...refFormData, tag: e.target.value })}
                      placeholder={t("decor.editor.tagPlaceholder")}
                    />
                  </label>
                </>
              )}

              <label>
                <span>{t("decor.editor.descriptionOptional")}</span>
                <textarea
                  value={refFormData.description}
                  onChange={(e) => setRefFormData({ ...refFormData, description: e.target.value })}
                  placeholder={t("decor.editor.notesPersonal")}
                  rows={3}
                />
              </label>
            </div>
            <div className="decor-modal-footer">
              <button type="button" className="decor-secondary" onClick={() => setShowRefModal(false)}>
                {t("common.cancel")}
              </button>
              <button type="button" className="decor-primary" onClick={handleSaveReference}>
                {refModalMode === "create" ? t("decor.editor.create") : t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {zoomItem && (
        <div className="decor-zoom-overlay" onClick={() => setZoomItem(null)}>
          <div className="decor-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="decor-zoom-close"
              onClick={() => setZoomItem(null)}
              aria-label="Fermer"
            >
              <span className="material-symbols-rounded">close</span>
            </button>
            <img src={zoomItem.url} className="decor-zoom-img" alt="" />
          </div>
        </div>
      )}
    </div>
  );
}
